import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { cx } from '@bolteu/clay-react';
import { AccountStage } from '@provider-portal/common/services/types/account';
import { TabHeader } from 'components/merchant-portal-2/TabHeader/TabHeader';
import { BoltStarDialogs } from 'widgets/BoltStarDialogs';
import { CustomerSupport } from 'widgets/CustomerSupport/CustomerSupport';
import { HandHoldAgent } from 'widgets/HandHoldAgent/HandHoldAgent';
import { OnboardingProviderSupport } from 'widgets/OnboardingProvidersSupport/OnboardingProviderSupport';

import { selectAccountStageOrNewMerchantVenueOnboarding } from 'rdx/account';

import { ActiveTabHeader } from 'root/navigation/navStructureUtils';
import { usePathAwareDocumentTitle } from 'root/navigation/usePathAwareDocumentTitle';
import { ReturnToOnboardingLink } from 'root/components/ReturnToOnboardingLink/ReturnToOnboardingLink';
import { useHasPermanentNavBar } from 'components/merchant-portal-2/NavBar/useHasPermanentNavBar';

// Extend Window interface for Microsoft Clarity
declare global {
  interface Window {
    clarity?: (action: string, key: string, value: string) => void;
  }
}

interface MainContentProps {
  children: React.ReactNode;
  activeTabHeader: ActiveTabHeader | null;
}

export function MainContent({ children, activeTabHeader }: MainContentProps) {
  usePathAwareDocumentTitle();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const intl = useIntl();
  const accountStage = useSelector(selectAccountStageOrNewMerchantVenueOnboarding);

  const setRef = useCallback((ref: HTMLElement | null) => {
    setContainerRef(ref);
  }, []);

  useEffect(() => {
    if (containerRef) {
      const scrollContainer = containerRef as HTMLElement;
      scrollContainer.scrollTop = 0;
    }
  }, [containerRef, location.pathname]);

  const isAccountActivated = useMemo(() => accountStage === AccountStage.ACTIVATED, [accountStage]);

  const hasPermanentNavBar = useHasPermanentNavBar();

  if (window.clarity && process.env.REACT_APP_STAGE === 'live') {
    window.clarity('set', 'current_ui_version', 'newVersion');
  }

  return (
    <main data-testid="home-screen" ref={setRef} className="mainContent">
      <div
        className={cx(
          'max-md:px-4 max-md:py-3 md:px-8 md:pt-6 md:pb-8',
          hasPermanentNavBar && 'ml-[228px]',
        )}
      >
        <ReturnToOnboardingLink />
        {activeTabHeader && <TabHeader {...activeTabHeader} />}
        <div className="pageContent">
          <div>{children}</div>
        </div>
      </div>
      {isAccountActivated ? <CustomerSupport /> : <OnboardingProviderSupport />}
      <BoltStarDialogs />
      {isAccountActivated && <HandHoldAgent locale={intl.locale} />}
    </main>
  );
}
